import React, { createContext, useReducer, useEffect, useCallback } from 'react';
import { login, logout, getCurrentUser, syncUserFromFirebaseSession, clearAuthSession } from '../services/authApi';
import { auth } from '../firebase';

const AuthContext = createContext();

const AUTH_ACTIONS = {
  AUTH_START: 'AUTH_START',
  AUTH_SUCCESS: 'AUTH_SUCCESS',
  AUTH_ERROR: 'AUTH_ERROR',
  AUTH_LOGOUT: 'AUTH_LOGOUT',
  CLEAR_ERROR: 'CLEAR_ERROR',
  SET_USER: 'SET_USER'
};

function authReducer(state, action) {
  switch (action.type) {
    case AUTH_ACTIONS.AUTH_START:
      return {
        ...state,
        loading: true,
        error: null
      };

    case AUTH_ACTIONS.AUTH_SUCCESS:
      return {
        ...state,
        loading: false,
        error: null,
        user: action.payload
      };

    case AUTH_ACTIONS.AUTH_ERROR:
      return {
        ...state,
        loading: false,
        error: action.payload,
        user: null
      };

    case AUTH_ACTIONS.AUTH_LOGOUT:
      return {
        ...state,
        loading: false,
        error: null,
        user: null
      };

    case AUTH_ACTIONS.SET_USER:
      return {
        ...state,
        user: action.payload
      };

    case AUTH_ACTIONS.CLEAR_ERROR:
      return {
        ...state,
        error: null
      };

    default:
      return state;
  }
}

const initialState = {
  user: null,
  loading: true,
  error: null
};

export function AuthProvider({ children }) {
  const [state, dispatch] = useReducer(authReducer, initialState);
  const isAuthenticatingRef = React.useRef(false);

  useEffect(() => {
    const forceLogout = async () => {
      clearAuthSession();
      await auth.signOut();
      dispatch({ type: AUTH_ACTIONS.AUTH_LOGOUT });
    };

    const unsubscribe = auth.onAuthStateChanged(async (firebaseUser) => {
      if (firebaseUser) {
        try {
          const currentUser = await getCurrentUser();
          dispatch({ type: AUTH_ACTIONS.AUTH_SUCCESS, payload: currentUser });
        } catch (err) {
          console.error("Erreur de chargement utilisateur:", err);
          
          // Parse error message
          const errorString = err.message || '';
          
          if (errorString.includes('401') || errorString.includes('Unauthorized')) {
            try {
              await firebaseUser.getIdToken(true);
              const currentUser = await getCurrentUser();
              dispatch({ type: AUTH_ACTIONS.AUTH_SUCCESS, payload: currentUser });
              return;
            } catch {
              if (!isAuthenticatingRef.current) {
                try {
                  const recoveredUser = await syncUserFromFirebaseSession(firebaseUser);
                  dispatch({ type: AUTH_ACTIONS.AUTH_SUCCESS, payload: recoveredUser });
                  return;
                } catch {}
              }
            }
            await forceLogout();
          }
          else if (errorString.includes('403') || errorString.includes('Forbidden') || errorString.includes('404')) {
            await forceLogout();
          } else {
            dispatch({ type: AUTH_ACTIONS.AUTH_SUCCESS, payload: null });
          }
        }
      } else {
        dispatch({ type: AUTH_ACTIONS.AUTH_SUCCESS, payload: null });
      }
    });

    return () => {
      unsubscribe();
    };
  }, []);

  const handleLogin = async (email, password) => {
    dispatch({ type: AUTH_ACTIONS.AUTH_START });
    isAuthenticatingRef.current = true;
    try {
      const userData = await login(email, password);
      dispatch({ type: AUTH_ACTIONS.AUTH_SUCCESS, payload: userData });
    } catch (err) {
      dispatch({ type: AUTH_ACTIONS.AUTH_ERROR, payload: err.message });
      throw err;
    } finally {
      isAuthenticatingRef.current = false;
    }
  };


  const handleLogout = async () => {
    await logout();
    dispatch({ type: AUTH_ACTIONS.AUTH_LOGOUT });
  };

  const refetchUser = useCallback(async () => {
    if (state.user) {
      try {
        const freshUser = await getCurrentUser();
        dispatch({ type: AUTH_ACTIONS.SET_USER, payload: freshUser });
      } catch (err) {
        dispatch({ type: AUTH_ACTIONS.AUTH_ERROR, payload: err.message });
      }
    }
  }, [state.user]);

  const value = {
    user: state.user,
    loading: state.loading,
    error: state.error,
    handleLogin,
    handleLogout,
    refetchUser,
    clearError: () => dispatch({ type: AUTH_ACTIONS.CLEAR_ERROR })
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}

export const useAuth = () => React.useContext(AuthContext);
